import GlobalStyle from '@/components/GlobalStyles.styled'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import ExperimentContext from '@/context/ExperimentContext'
import handleErrorBoundaryLogging from '@/shared-utils/handle-error-boundary-logging'
import type { BaseSetiResponseEvent, BootstrapData, Referral, Webstats } from '@/types'
import ErrorBoundary from '@pcln/error-boundary'
import pclnExpt from '@pcln/experimentation'
import { mark, tag } from '@pcln/measure'
import type { AppProps } from 'next/app'
import { ThemeProvider } from 'pcln-design-system'
import { useEffect, useLayoutEffect } from 'react'
import '../styles/global.css'

const PRODUCT_CODES = {
  flights: 1,
  hotels: 5,
  packages: 17,
  home: 16,
  vip: 16,
  join: 16,
  ccpa: 16,
} as const

function Redirect() {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      window.location.href = '/pages/homepage_lite.html?reason=hp_react_boundary'
    }
  }, [])
  return null
}

export default function MyApp({ Component, pageProps }: AppProps) {
  const bootstrapData = pageProps?.bootstrapData as BootstrapData | undefined
  useEffect(() => {
    mark('next-landing visual paint complete')
  })

  useLayoutEffect(() => {
    if (bootstrapData) {
      tag('appName', bootstrapData.appName || 'next-landing')
      tag('appVersion', bootstrapData.appVersion)
      tag('signInStatus', bootstrapData.signInStatus)
    }
  }, [bootstrapData])

  useLayoutEffect(() => {
    function clearBatchHandler() {
      pclnExpt.clearBatch()
    }
    function initializePclnExpt({
      fullSETI = [],
      isMobile = false,
      key: productCode,
      referral,
      webstats,
    }: {
      fullSETI: ReadonlyArray<BaseSetiResponseEvent>
      isMobile: boolean
      key: keyof typeof PRODUCT_CODES
      referral: Referral
      webstats: Webstats
    }): void {
      const webAppMetadata = {
        appCode: isMobile ? 'MOBILEWEB' : 'DESKTOPWEB',
        clientIP: webstats.resolvedClientIP,
        globalPartnerCode: 'PCLN',
        partnerCode: 'pcln',
        productID: PRODUCT_CODES[productCode],
        productSubType: 'ALL',
        referralClickID: referral.clickId,
        referralID: referral.id,
        referralSourceID: referral.sourceId,
        serverTimestamp: webstats.requestTime,
      }
      pclnExpt.initialize(fullSETI, webAppMetadata, {
        useBatching: true,
      })
    }
    if (bootstrapData) {
      initializePclnExpt(bootstrapData)
      window.addEventListener('beforeunload', clearBatchHandler)
    }
    return () => {
      if (bootstrapData) {
        window.removeEventListener('beforeunload', clearBatchHandler)
      }
    }
  }, [bootstrapData])

  return (
    <ErrorBoundary
      log={handleErrorBoundaryLogging}
      payload={{
        severity: 1,
        componentName: '_app.tsx',
        message: 'Error raised by React ErrorBoundary',
      }}
      fallback={<Redirect />}
    >
      <ThemeProvider>
        <GlobalStyle />
        <link rel='preconnect' href='https://fonts.googleapis.com' crossOrigin='anonymous' />
        {bootstrapData ? (
          <BootstrapDataContext.Provider value={bootstrapData}>
            <ExperimentContext.Provider value={bootstrapData.seti}>
              <Component {...pageProps} />
            </ExperimentContext.Provider>
          </BootstrapDataContext.Provider>
        ) : (
          <Component {...pageProps} />
        )}
      </ThemeProvider>
    </ErrorBoundary>
  )
}
